import React from 'react';
import FieldTypesEnum from '../../Enums/FieldTypesEnum';
import JsxHelper from '../../Helpers/JsxHelper';
import UserNameComponent from '../../Elements/UserNameComponent';
import DataTableActionMenu from '../../Elements/DataTable/DataTableActionMenu';
import urlHelper from '../../Helpers/urlHelper';
import {
  isEntryLocked,
  canEntryBeCancelled,
  hasLiveBonusShare,
  getReverseIobInvoiceIds,
  mapBaseFields,
  initBaseFields,
  formatAuditTooltip,
} from './outsourcedUserFteHelpers';

// A reversed row keeps Status = Invoiced and its invoice links, so this badge is the only visible sign of the
// reversal.
function renderReversedBadge(item) {
  return item.ReversedOnUtc ? (
    <span
      className='text-danger ms-1'
      title={formatAuditTooltip('IOB reversed', item.ReversedOnUtc, item.ReversedByName)}
    >
      <i className='fa-solid fa-rotate-left me-1'></i>Reversed
    </span>
  ) : null;
}

// A bonus invoice link stays visible after its shares are reversed, on their own or with the row, so it gets
// its own badge. Reversing the row reverses its bonus too, which also covers shares reversed with a row before
// the shares carried their own markers.
function renderBonusReversedBadge(item, bonuses) {
  const reversedBonus = bonuses.find((bonus) => bonus.reversedOnUtc);
  const allReversed = bonuses.every((bonus) => bonus.reversedOnUtc);
  if (!allReversed && !item.ReversedOnUtc) return null;
  const tooltip = reversedBonus
    ? formatAuditTooltip('Bonus IOB reversed', reversedBonus.reversedOnUtc, reversedBonus.reversedByName)
    : formatAuditTooltip('IOB reversed', item.ReversedOnUtc, item.ReversedByName);
  return (
    <span
      className='text-danger ms-1'
      title={tooltip}
    >
      <i className='fa-solid fa-rotate-left me-1'></i>Reversed
    </span>
  );
}

export default class FteTrackingItem {
  constructor(item) {
    if (item === undefined) {
      initBaseFields(this);
      this.UserOfficeId = '';
      this.UserOfficeName = '';
      this.IoblineItemId = null;
      this.IobinvoiceId = null;
      this.ReverseIobInvoiceId = null;
      this.BonusIobs = [];
      this.Selected = false;
      return;
    }

    mapBaseFields(this, item);
    this.UserOfficeId = item.userOfficeId;
    this.UserOfficeName = item.userOfficeName;
    this.IoblineItemId = item.ioblineItemId;
    this.IobinvoiceId = item.iobinvoiceId;
    this.ReverseIobInvoiceId = item.reverseIobInvoiceId;
    this.BonusIobs = item.bonusIobs || [];
    this.Selected = false;
  }

  static displayedInList = 'FieldworkOfficeName';

  static NameOfTheIdField() {
    return 'OutsourcedUserFteid';
  }

  static MoneyFields = [];
  static Currency() {
    return '';
  }
  static MoneyFieldsSecondary = [];
  static CurrencySecondary() {
    return '';
  }
  static DateOnlyFields = [];
  static DateFields = [];
  static BoolFields = [];

  static nameOfTheItemDetailList(item) {
    if (item) return <span>FTE Entry - {item.FieldworkOfficeName}</span>;
    return 'FTE Entry Details';
  }

  static editFunction = (id, item) => {
    alert('Empty function');
  };

  static deleteFunction = (item) => {
    alert('Empty function');
  };

  static lockFunction = (item) => {
    alert('Empty function');
  };

  static cancelFunction = (item) => {
    alert('Empty function');
  };

  static reverseIobFunction = (item) => {
    alert('Empty function');
  };

  static reverseBonusIobFunction = (item) => {
    alert('Empty function');
  };

  static clickOnSelect(item, selected) {
    //This is a placeholder, don't remove it!
  }

  static clickOnGlobalSelect(item, selected) {
    //This is a placeholder, don't remove it!
  }

  static selectedStatus() {
    return false;
  }

  static getSelectedCellHeader() {
    return (
      <div className='text-left'>
        <input
          type='checkbox'
          checked={FteTrackingItem.selectedStatus()}
          onChange={(e) => {
            FteTrackingItem.clickOnGlobalSelect(null, e.target.checked);
          }}
        />
      </div>
    );
  }

  static getSelectedCellContent(item) {
    return (
      <div className='text-left'>
        <input
          type='checkbox'
          checked={item.Selected}
          onChange={(e) => {
            FteTrackingItem.clickOnSelect(item, e.target.checked);
          }}
        />
      </div>
    );
  }

  static getActionsContent(item) {
    const locked = isEntryLocked(item);
    const isInvoiced = item.Status === 'Invoiced' || !!item.IoblineItemId;
    const isReversed = !!item.ReversedOnUtc;
    return (
      <DataTableActionMenu
        options={[
          {
            iconName: 'fa-solid fa-edit',
            text: 'Edit',
            onClick: () => FteTrackingItem.editFunction(item.OutsourcedUserFteid, item),
            roleKey: 'outsourcedfte_edit',
            disabled: locked,
          },
          {
            iconName: 'fa-solid fa-lock',
            text: 'Lock',
            onClick: () => FteTrackingItem.lockFunction(item),
            roleKey: 'outsourcedfte_lock',
            disabled: locked,
          },
          {
            iconName: 'fa-solid fa-ban',
            text: 'Cancel Entry',
            onClick: () => FteTrackingItem.cancelFunction(item),
            roleKey: 'outsourcedfte_lock',
            disabled: !canEntryBeCancelled(item),
          },
          {
            iconName: 'fa-solid fa-rotate-left',
            text: 'Create Reverse IOB',
            onClick: () => FteTrackingItem.reverseIobFunction(item),
            roleKey: 'outsourcedfte_iobreverse',
            disabled: !isInvoiced || isReversed,
          },
          {
            iconName: 'fa-solid fa-gift',
            text: 'Create Reverse Bonus IOB',
            onClick: () => FteTrackingItem.reverseBonusIobFunction(item),
            roleKey: 'outsourcedfte_iobreverse',
            disabled: !hasLiveBonusShare(item),
          },
          {
            iconName: 'fa-regular fa-trash-can',
            text: 'Delete',
            onClick: () => FteTrackingItem.deleteFunction(item),
            roleKey: 'outsourcedfte_edit',
            disabled: locked,
          },
        ]}
      />
    );
  }

  static FieldsToShowInDataTable() {
    return [
      {
        label: FteTrackingItem.getSelectedCellHeader,
        orderById: '',
        data: FteTrackingItem.getSelectedCellContent,
        minWidth: 10,
      },
      {
        label: 'Name',
        orderById: 'UserName',
        data: (item) => <UserNameComponent userId={item.UserId} />,
        type: FieldTypesEnum.PureDataField,
      },
      {
        label: 'FTE',
        orderById: 'Ftepercentage',
        // Ftepercentage is a percentage (0–100); show it as a fraction (50 -> 0.5) in this grid.
        data: (item) => (item.Ftepercentage == null ? '' : `${item.Ftepercentage / 100}`),
        type: FieldTypesEnum.PureDataField,
      },
      {
        label: 'Hiring Office',
        orderById: '',
        data: 'HiringOfficeName',
        type: FieldTypesEnum.PureDataField,
      },
      {
        label: 'Bill to Office',
        orderById: 'FieldworkOfficeName',
        data: 'FieldworkOfficeName',
        type: FieldTypesEnum.PureDataField,
      },
      {
        label: 'Start Date',
        orderById: 'StartDateUtc',
        data: 'StartDateUtc',
        type: FieldTypesEnum.PureDataField,
      },
      {
        label: 'End Date',
        orderById: 'EndDateUtc',
        data: 'EndDateUtc',
        type: FieldTypesEnum.PureDataField,
      },
      {
        label: 'Status',
        orderById: 'Status',
        data: (item) => {
          if (item.Status === 'Invoiced' && item.IobinvoiceId) {
            return (
              <span>
                <i className='fa-solid fa-file-invoice-dollar text-success me-1'></i>
                {urlHelper.getIconLinkComponentFigma(
                  `IOB #${item.IobinvoiceId}`,
                  'fa-solid fa-arrow-up-right-from-square',
                  'text-success',
                  'iobdetails',
                  `id=${item.IobinvoiceId}`,
                  'Open IOB Invoice'
                )}
                {renderReversedBadge(item)}
              </span>
            );
          }
          if (item.Status === 'Locked') {
            return (
              <span>
                <i className='fa-solid fa-lock text-warning me-1'></i>Locked
              </span>
            );
          }
          if (item.Status === 'Open') {
            return (
              <span>
                <i className='fa-solid fa-lock-open text-warning me-1'></i>Open
              </span>
            );
          }
          if (item.Status === 'Confirmed') {
            return (
              <span>
                <i className='fa-solid fa-circle-check text-success me-1'></i>Confirmed
              </span>
            );
          }
          if (item.Status === 'Cancelled') {
            return (
              <span title={formatAuditTooltip('Cancelled', item.CancelledOnUtc, item.CancelledByName)}>
                <i className='fa-solid fa-ban text-danger me-1'></i>Cancelled
              </span>
            );
          }
          return item.Status;
        },
        type: FieldTypesEnum.PureDataField,
      },
      {
        label: 'Bonus IOB',
        orderById: '',
        data: (item) => {
          if (!item.BonusIobs || item.BonusIobs.length === 0) return null;
          // One link per distinct bonus invoice; the tooltip lists the bonus type(s) on it. Amounts are
          // deliberately NOT shown (or shipped to the grid at all): the IOB line items are gated by the
          // OutsourcedFteView role, and the grid is visible without it.
          const byInvoice = new Map();
          item.BonusIobs.forEach((b) => {
            if (!b.iobInvoiceId) return;
            if (!byInvoice.has(b.iobInvoiceId)) byInvoice.set(b.iobInvoiceId, []);
            byInvoice.get(b.iobInvoiceId).push(b);
          });
          return (
            <span>
              {[...byInvoice.entries()].map(([invoiceId, bonuses]) => (
                <span
                  key={invoiceId}
                  className='me-2'
                >
                  <i className='fa-solid fa-gift text-success me-1'></i>
                  {urlHelper.getIconLinkComponentFigma(
                    `IOB #${invoiceId}`,
                    'fa-solid fa-arrow-up-right-from-square',
                    'text-success',
                    'iobdetails',
                    `id=${invoiceId}`,
                    bonuses.map((b) => b.bonusReason).join(' | ')
                  )}
                  {renderBonusReversedBadge(item, bonuses)}
                </span>
              ))}
            </span>
          );
        },
        type: FieldTypesEnum.PureDataField,
      },
      {
        label: 'Reverse IOB',
        orderById: '',
        data: (item) => {
          const reverseIobInvoiceIds = getReverseIobInvoiceIds(item);
          if (reverseIobInvoiceIds.length === 0) return null;
          return (
            <span>
              {reverseIobInvoiceIds.map((invoiceId) => (
                <span
                  key={invoiceId}
                  className='me-2'
                >
                  <i className='fa-solid fa-file-invoice-dollar text-danger me-1'></i>
                  {urlHelper.getIconLinkComponentFigma(
                    `IOB #${invoiceId}`,
                    'fa-solid fa-arrow-up-right-from-square',
                    'text-danger',
                    'iobdetails',
                    `id=${invoiceId}`,
                    'Open Reverse IOB'
                  )}
                </span>
              ))}
            </span>
          );
        },
        type: FieldTypesEnum.PureDataField,
      },
      {
        label: ' ',
        orderById: '',
        data: FteTrackingItem.getActionsContent,
        type: FieldTypesEnum.ActionButton,
      },
    ];
  }

  static FieldsToShowInListTable = [];
}
